import type { BotDoc, SceneDoc, StoryRunDoc } from '@spirits/shared';
import { FunctionsError, httpsCallable } from 'firebase/functions';
import { useEffect, useRef, useState } from 'react';
import { functions } from '../firebase';
import { withLocks } from './botOrder';

const resetStoryCallable = httpsCallable<
  { storyId: string },
  { ok: boolean; storyId: string; currentSceneId: string }
>(functions, 'resetStory');

const ACTION_EMOJI: Record<string, string> = {
  idle: '🙂', cooking: '🍳', sleeping: '😴', pacing: '🚶', reading: '📖', working: '💻',
};

export type FeedSelection = { kind: 'scene' | 'bot' | 'ending'; id: string } | null;

interface Props {
  story: StoryRunDoc & { id: string };
  scenes: (SceneDoc & { id: string })[];
  bots: (BotDoc & { id: string })[];
  selected: FeedSelection;
  onSelect: (sel: FeedSelection) => void;
}

// The "Storyline": one flat chronological feed of pages — each page is a scene
// (its setting) or a character. Oldest at top, newest appended at the bottom and
// auto-scrolled into view. A character's page only appears once it's their turn
// (decided, or the one currently up); waiting characters pop in as new pages.
export function SceneFeed({ story, scenes, bots, selected, onSelect }: Props) {
  const [resetOpen, setResetOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const ordered = [...scenes].sort((a, b) => a.order - b.order); // oldest → newest
  const decidedCount = bots.filter((b) => b.decisionState === 'decided').length;

  // Keep the newest pages in view as the story unfolds (new scene, or a new
  // character's page appearing when their turn comes).
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [story.currentSceneId, decidedCount, scenes.length, story.status]);

  return (
    <div className="space-y-1.5">
      {ordered.map((scene) => {
        const isCurrent = scene.id === story.currentSceneId;
        const sceneBots = withLocks(bots.filter((b) => b.sceneId === scene.id));
        const visible = sceneBots.filter((o) => !o.locked); // hide characters still waiting
        const allDecided = sceneBots.length > 0 && sceneBots.every((o) => o.bot.decisionState === 'decided');
        const sceneActive = selected?.kind === 'scene' && selected.id === scene.id;

        return (
          <div key={scene.id} className="space-y-1.5">
            {/* Scene page */}
            <button
              type="button"
              onClick={() => onSelect({ kind: 'scene', id: scene.id })}
              className={`w-full text-left rounded-lg border px-3 py-2 transition-colors cursor-pointer ${
                sceneActive
                  ? 'border-amber-400/60 bg-amber-950/20'
                  : isCurrent
                    ? 'border-amber-500/30 bg-amber-950/10 hover:border-amber-500/50'
                    : 'border-slate-800 bg-slate-900/30 hover:border-slate-600'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] uppercase tracking-[0.18em] text-amber-300/70">
                  Scene {scene.order + 1}
                  <span className={`ml-1.5 ${isCurrent ? 'text-emerald-300/70' : 'text-slate-500'}`}>
                    {isCurrent ? '· now' : '· past'}
                  </span>
                </span>
                {scene.stage && (
                  <span className="flex-none text-[9px] uppercase tracking-[0.15em] text-violet-300/60" title="Story Mountain stage">
                    {scene.stage}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-100 leading-snug truncate">{scene.name}</p>
            </button>

            {/* Character pages (only those whose turn has come) */}
            {visible.map(({ bot }) => {
              const botActive = selected?.kind === 'bot' && selected.id === bot.id;
              const decided = bot.decisionState === 'decided';
              return (
                <button
                  key={bot.id}
                  type="button"
                  onClick={() => onSelect({ kind: 'bot', id: bot.id })}
                  className={`w-full text-left rounded-lg border px-3 py-2 ml-3 transition-colors cursor-pointer ${
                    botActive
                      ? 'border-amber-400/60 bg-amber-950/20'
                      : decided
                        ? 'border-emerald-700/40 bg-emerald-950/10 hover:border-emerald-600/60'
                        : 'border-slate-800 bg-slate-900/40 hover:border-slate-600'
                  }`}
                  style={{ width: 'calc(100% - 0.75rem)' }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="text-sm leading-none">🧑</span>
                      <span className="text-sm text-slate-100 truncate">{bot.displayName}</span>
                      <span className="text-xs leading-none">{ACTION_EMOJI[bot.actionState] ?? '🙂'}</span>
                    </span>
                    <span className={`text-[10px] uppercase tracking-widest flex-none ${decided ? 'text-emerald-400/70' : 'text-amber-300/70'}`}>
                      {decided ? 'decided' : 'deciding'}
                    </span>
                  </div>
                  {decided && bot.committedAction && (
                    <p className="mt-0.5 text-xs text-emerald-300/70 leading-snug truncate">
                      → {bot.committedAction.label}
                    </p>
                  )}
                </button>
              );
            })}

            {/* Turn-the-page prompt sits at the end of the current scene */}
            {isCurrent && allDecided && !story.generating && (
              <div className="ml-3 px-3 py-3 text-center space-y-2" style={{ width: 'calc(100% - 0.75rem)' }}>
                <p className="text-xs text-amber-200/90 leading-snug">Everyone has chosen.</p>
                {/* <GenerateNextButton storyId={story.id} /> */}
              </div>
            )}
          </div>
        );
      })}

      {/* Ending page — the recap, available once the run is complete */}
      {story.status === 'complete' && (
        <button
          type="button"
          onClick={() => onSelect({ kind: 'ending', id: story.id })}
          className={`w-full text-left rounded-lg border px-3 py-2 transition-colors cursor-pointer ${
            selected?.kind === 'ending'
              ? 'border-amber-400/60 bg-amber-950/20'
              : 'border-amber-500/30 bg-amber-950/10 hover:border-amber-500/50'
          }`}
        >
          <span className="text-[10px] uppercase tracking-[0.18em] text-amber-300/70">The ending ✦</span>
          <p className="text-sm text-slate-100 leading-snug truncate">
            {story.recap?.title ?? 'The story is told'}
          </p>
        </button>
      )}

      <div ref={bottomRef} />

      <button
        type="button"
        onClick={() => setResetOpen(true)}
        className="text-[11px] text-slate-600 hover:text-slate-400 underline-offset-2 hover:underline p-4"
      >
        ↺ restart this run
      </button>

      {resetOpen && (
        <RestartDialog
          storyId={story.id}
          title={story.title}
          onClose={() => setResetOpen(false)}
          onReset={() => {
            onSelect(null);
            setResetOpen(false);
          }}
        />
      )}
    </div>
  );
}

function RestartDialog({
  storyId,
  title,
  onClose,
  onReset,
}: {
  storyId: string;
  title: string;
  onClose: () => void;
  onReset: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reset() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await resetStoryCallable({ storyId });
      onReset();
    } catch (e) {
      setError((e as FunctionsError)?.message ?? 'could not restart');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-xl">
        <h2 className="text-lg text-slate-100">Restart “{title}”?</h2>
        <p className="mt-1.5 text-sm text-slate-400">
          This throws away every generated scene and choice and returns the cast to the opening. The
          chat is wiped. Essence you've spent is <span className="text-rose-300">not refunded</span>.
        </p>
        {error && <p className="mt-2 text-xs text-rose-400">{error}</p>}
        <div className="mt-5 flex justify-end gap-3 text-sm">
          <button type="button" onClick={onClose} className="px-4 py-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-slate-200">
            cancel
          </button>
          <button type="button" onClick={reset} disabled={busy} className="px-4 py-1.5 rounded-lg border border-rose-500/60 text-rose-100 bg-rose-900/40 hover:bg-rose-800/60 disabled:opacity-50">
            {busy ? 'restarting…' : '↺ yes, restart'}
          </button>
        </div>
      </div>
    </div>
  );
}
